// ────────────────────────────────────────────────────────────────────
// WHAT AN EMPTY PANEL ON THIS SCREEN IS ALLOWED TO SAY.
//
// Three of the handoff's panels are drawn over tables that NOTHING WRITES TO:
//
//   • `AssetRecommendation` (server/models.py:1121) — no reader, no writer, no
//     route. The only Python that names it is the migration that created the
//     table and the RLS tests that prove the table is covered.
//   • `AssetRepair` (server/models.py:1108) — the same.
//   • `InspectionObservation` (server/models.py:1134) — read by
//     `assemble.attach_detail`, written by nothing: no importer, no form.
//
// So all three render empty on production. The alternative considered on
// 2026-08-31 was omitting the panels until a writer exists, and it was
// rejected: a missing panel says nothing, while an honest one says exactly
// what is missing and makes the gap visible instead of invisible. It is the
// same principle as publishing every interval candidate — the screen's job is
// to be honest about what it knows.
//
// THE RULING IS ABOUT THE WORDING, not about the element. "No data" is what a
// broken screen says; these say which of the two it is. The wording is
// asserted in the tests as literal text for exactly that reason: an empty
// state that decays into "—" or "None" has lost the only thing it was for.
//
// This is NOT the sentence to reach for when a table that DOES have a writer
// happens to be empty. That is an absence, and it reads "none recorded on this
// inspection".
// ────────────────────────────────────────────────────────────────────

/** The lead sentence all three share, so the gap reads as one gap. */
export const NOT_CAPTURED = "The system does not capture this yet.";

export const RECOMMENDATIONS_NOT_CAPTURED =
  `${NOT_CAPTURED} An asset can hold recommendations, and nothing in the product writes one — `
  + "no importer, no form, no agent — so this panel is empty on every asset and not only on "
  + "this one. It is drawn anyway, because a recommendation that lives in a signed report and "
  + "nowhere else is invisible to whoever picks the asset up next.";

export const REPAIRS_NOT_CAPTURED =
  `${NOT_CAPTURED} Repairs, alterations and re-ratings have a place on the asset record, and `
  + "nothing in the product writes one. When one is recorded it belongs here rather than in the "
  + "job that found it, because the next engineer needs to know the metal was changed before "
  + "reading a rate off it.";

export const MEASUREMENTS_NOT_CAPTURED =
  `${NOT_CAPTURED} Settlement, coating, CP potential, MAWP and the rest are read from the `
  + "record, and nothing in the product writes one — no importer, no form. They produce no "
  + "corrosion rate and they still decide things, which is why the panel stays on screen "
  + "saying so.";

/** For a table that HAS a writer and simply holds nothing for this inspection. */
export const NONE_ON_THIS_INSPECTION = "none recorded on this inspection";

// ── the raw-file modal's two gaps (./RawFileModal) ──────────────────
//
// The same ruling, one level down: the handoff draws two bodies over data
// that no part of the product produces. Both say which of those it is.

/** The handoff's SCAN body is a 32 × 12 grid of wall-loss cells. Nothing in
 *  the tree produces one, so the grid is not drawn. Asserted as wording by
 *  RawFileModal.test.tsx precisely so that a later contributor cannot quietly
 *  fabricate a grid to fill the space. */
export const SCAN_GRID_NOT_CAPTURED =
  `${NOT_CAPTURED} A scan's wall-loss grid — the 32 × 12 map the handoff draws here — has no `
  + "source anywhere in the product: no importer reads one, no table holds one, and no endpoint "
  + "returns one. A grid drawn from nothing is a set of measurements nobody took, so none is "
  + "drawn. The scan file itself is held against this inspection and opens in the tool that "
  + "wrote it.";

/** The handoff's PHOTOS body labels each frame with a grid reference. An
 *  intake file is one object in storage, and no table records where a
 *  photograph was taken. */
export const PHOTO_FRAMES_NOT_CAPTURED =
  `${NOT_CAPTURED} An intake file is one object in storage — a photo file is one photograph — `
  + "and nothing records which CML or grid reference it was taken at. No frame is drawn here, "
  + "because a grid reference invented in the browser is a location nobody surveyed.";

/** PDF / DIFF / DOC with nothing read off the intake row. An absence, not a
 *  missing writer: the intake row exists and simply came through empty. */
export const FILE_PROPERTIES_NOT_READ =
  "no properties were read off this file's record";
